import classNames from 'classnames';
import he from 'he';
import fp from 'lodash/fp';
import queryString from 'query-string';
import React from 'react';
import { Button, Col, Grid, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Prompt } from 'react-router';
import { push } from 'react-router-redux';
import { lifecycle, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators, compose } from 'redux';
import { contentDisplayView, contentStatus, PublishedEventTypes, routes } from '../../../../constants';
import { eventActions } from '../../../../state/modules/event';
import { currentUserSelector } from '../../../../state/modules/user/selectors';
import { toSafeExternalUrl } from '../../../../utils';
import { ConfirmationModal } from '../../../components/';
import Loading from '../../loading';

const eventRedirectConfig = {
  eventRegistrationStatus: {
    Registered: "Registered",
    NotRegistered: "Not registered",
    Confirming: "Confirming",
    ShouldRegister: "Should register",
    ShouldNotRegister: "Should not register"
  },
  confirmRegister: {
    message: <p>We'll be sure to remind you to record your CPD for this event after it happens.</p>,
    heading: <span>Did you register for this event?</span>,
    confirmLabel: "Yes",
    cancelLabel: "No"
  }
}

const CourseConfirmationModal = ({
  register,
  notAttend,
  cancel,
  show
}) => (
    <Modal show={show} onHide={cancel}>
      <Modal.Header>
        <Modal.Title componentClass='h2' className='text-center'>
          <span>Do you want to register for this course?</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='text-center'>
        <p>We'll be sure to remind you to record your CPD for this course after it happens.</p>
      </Modal.Body>
      <Modal.Footer className='text-center confirmation-footer'>
        <Row>
          <Col xs={12}>
            <Button onClick={register}>
              {"Yes, Register for course"}
            </Button>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Button className='btn-delete' onClick={notAttend}>
              {"No, I'm not attending"}
            </Button>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Button bsStyle='plain' onClick={cancel}>
              {"I'll come back later"}
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );

const EventRedirect = ({
  displayView,
  selectedEvent,
  setDisplayView,
  goTo,
  ecpdId,
  eventId,
  registerStatus,
  updateRegisterStatus,
  history,
  taskId,
  setNotAttendCourse,
  isPastEvent
}) => {
  const isCourse = fp.get('eventType')(selectedEvent) === PublishedEventTypes.Course
  const eventType = isCourse ? "course" : "event"
  const taskSearchQuery = + taskId ? `?${queryString.stringify({ taskId })}` : ''
  if (history.action === "PUSH") {
    history.go(0)
    return <div></div>
  } else {
    return (
      <div>
        {
          selectedEvent ?
            <div id='event'>
              <div
                id='event-container'
                className={classNames({ hidden: displayView !== contentDisplayView.content })}>
                <iframe
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  title={selectedEvent.title}
                  src={toSafeExternalUrl(he.decode(selectedEvent.link)) || 'about:blank'} />

              </div>
              <Grid className={classNames({
                hidden: displayView === contentDisplayView.content,
                prompt: true
              })}>
                <Row>
                  {
                    isCourse ?
                      <CourseConfirmationModal {...{
                        key: 0,
                        show: registerStatus === eventRedirectConfig.eventRegistrationStatus.Confirming,
                        register: () => {
                          goTo(routes.app.externalCpd.registerEvent(eventId + taskSearchQuery))
                        },
                        notAttend: () => {
                          const callback = () => {
                            if (taskId) {
                              goTo(routes.app.tasks.view(taskId))
                            } else {
                              goTo(routes.app.training.home)
                            }
                          }
                          setNotAttendCourse(eventId, callback)
                        },
                        cancel: () => {
                          if (taskId) {
                            goTo(routes.app.tasks.view(taskId))
                          } else {
                            goTo(routes.app.training.home)
                          }
                        }
                      }} /> :
                      <ConfirmationModal {...{
                        key: 0,
                        show: registerStatus === eventRedirectConfig.eventRegistrationStatus.Confirming,
                        actions: [{
                          onConfirmAction: () => {
                            goTo(routes.app.externalCpd.registerEvent(eventId))
                          },
                          actionLabel: eventRedirectConfig.confirmRegister.confirmLabel,
                        }],
                        cancel: () => {
                          goTo(routes.app.training.home)
                        },
                        message: eventRedirectConfig.confirmRegister.message,
                        headingText: eventRedirectConfig.confirmRegister.heading,
                        cancelLabel: eventRedirectConfig.confirmRegister.cancelLabel
                      }} />
                  }
                  <Prompt
                    when={registerStatus === eventRedirectConfig.eventRegistrationStatus.NotRegistered}
                    message={() => {
                      if (displayView !== contentDisplayView.content) {
                        return true;
                      } else {
                        updateRegisterStatus(eventRedirectConfig.eventRegistrationStatus.Confirming)
                        return false
                      }
                    }}
                  />
                  {
                    ecpdId && selectedEvent.status === contentStatus.Archived.value ?
                      <Col xs={12}>
                        <h2>This {eventType} has been archived.</h2>
                        <p>You have registered CPD points against this item and you can edit it below.</p>
                        <Button
                          className='eventButton'
                          onClick={() => goTo(routes.app.externalCpd.detailsEdit(ecpdId + taskSearchQuery))}>
                          Manage CPD for this {eventType}
                        </Button>
                      </Col>
                      :
                      isPastEvent ?
                        <Col xs={12}>
                          <h2>Did you attend this {eventType}?</h2>
                          <p>Remember to record your attendance as external CPD in Caddie if you ended up attending.</p>
                          <div>
                            <Button className='eventButton'
                              onClick={() => {
                                if (ecpdId) {
                                  goTo(routes.app.externalCpd.detailsEdit(ecpdId + taskSearchQuery))
                                } else {
                                  goTo(routes.app.externalCpd.registerEvent(eventId + taskSearchQuery))
                                }
                              }}>
                              Yes, record it in Caddie
                            </Button>
                          </div>
                          <p>
                            <Button
                              bsStyle='plain'
                              className='small narrow'
                              onClick={() => setDisplayView(contentDisplayView.content)}>
                              No, continue to event
                            </Button>
                          </p>
                        </Col> :
                        <Col xs={12}>
                          <h2>Interested in registering for this {eventType}?</h2>
                          <Button
                            className='eventButton'
                            onClick={() => setDisplayView(contentDisplayView.content)}>
                            Continue to {eventType}
                          </Button>
                          <p>
                            <Button bsStyle='plain' className='small narrow'
                              onClick={() => {
                                if (ecpdId) {
                                  goTo(routes.app.externalCpd.detailsEdit(ecpdId + taskSearchQuery))
                                } else {
                                  goTo(routes.app.externalCpd.registerEvent(eventId + taskSearchQuery))
                                }
                              }}>
                              Already registered? Record this as a pending activity here.
                            </Button>
                          </p>
                          <p>Remember to visit your CPD activity page in your Professional Profile to submit this activity if you attend.</p>
                        </Col>
                  }
                </Row>
              </Grid>
            </div> :
            <Loading />
        }
      </div>
    )
  }
};

const mapStateToProps = (state) => ({
  currentUser: currentUserSelector(state),
  selectedEvent: state.event.selectedEvent,
  contenetError: state.event.error,
  ecpdId: fp.first(state.event.eventRegistered)
});

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(push, dispatch),
  getEvent: bindActionCreators(eventActions.event.getById, dispatch),
  checkEventRegistered: bindActionCreators(eventActions.event.checkEventRegistered, dispatch),
  setNotAttendCourse: bindActionCreators(eventActions.event.setNotAttendCourse, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(({ ecpdId }) => ({
    displayView: contentDisplayView.info,
    earntMicroPoints: false,
    registerStatus: ecpdId ? eventRedirectConfig.eventRegistrationStatus.Registered : eventRedirectConfig.eventRegistrationStatus.NotRegistered,
  }), {
      setDisplayView: () => (displayView) => ({
        displayView
      }),
      updateRegisterStatus: () => (newStatus) => ({
        registerStatus: newStatus
      })
    }),
  withProps(({
    location: { search }
  }) => ({
    eventId: queryString.parse(search).eventId,
    taskId: queryString.parse(search).taskId
  })),
  lifecycle({
    componentDidMount() {
      const {
        getEvent,
        eventId,
        checkEventRegistered,
      } = this.props;

      if (eventId) {
        getEvent(eventId);
        checkEventRegistered(eventId);
      }
    },
    componentDidUpdate(prevProps) {
      const {
        registerStatus,
        updateRegisterStatus,
        ecpdId
      } = this.props

      if (registerStatus === eventRedirectConfig.eventRegistrationStatus.NotRegistered && ecpdId) {
        updateRegisterStatus(eventRedirectConfig.eventRegistrationStatus.Registered)
      }
    }
  }),
  withProps(
    ({ selectedEvent }) => ({
      isPastEvent: fp.flow(
        fp.get('dateTimeOfEvent'),
        (date) => {
          if (date) {
            return new Date(date).getTime()
          }
        }
      )(selectedEvent) < Date.now()
    })
  )
)(EventRedirect);



// WEBPACK FOOTER //
// ./src/containers/pages/app/training/event-redirect.js